import { useMemo } from 'react';
import { CHART, type Engine } from '@piaip/shared';
import { sx } from '../sx';
import { Empty } from './ui';

/**
 * Work done, time consumed and money released, week by week.
 *
 * The geometry — a 680×210 viewBox with a 36px left gutter and a 26px foot —
 * is the design's, so the same drawing serves the dashboard card and the
 * printed executive summary.
 */
export function ProgressCurve({ engine, print = false }: { engine: Engine; print?: boolean }) {
  const series = useMemo(() => engine.trendSeries(), [engine]);

  const geometry = useMemo(() => {
    const points = (key: 'actual' | 'time' | 'fin') =>
      series
        .map((p, i) => `${engine.xAt(i, series.length).toFixed(1)},${engine.yAt(p[key], 100).toFixed(1)}`)
        .join(' ');
    const actual = points('actual');
    const baseY = CHART.H - CHART.b;
    const area =
      series.length > 1
        ? `${engine.xAt(0, series.length).toFixed(1)},${baseY} ${actual} ${engine
            .xAt(series.length - 1, series.length)
            .toFixed(1)},${baseY}`
        : '';
    const step = Math.max(1, Math.ceil(series.length / 9));
    const labels = series
      .map((p, i) => ({ i, p }))
      .filter((o) => o.i % step === 0 || o.i === series.length - 1)
      .map((o) => ({ key: o.i, label: `W${o.p.no}` }));
    return { actual, time: points('time'), fin: points('fin'), area, labels };
  }, [engine, series]);

  if (series.length <= 1) {
    return print ? (
      <div style={sx('font-size:9.5pt;color:#5a6b68;padding:20px 0')}>
        The curve will appear once two weekly returns have been approved.
      </div>
    ) : (
      <Empty>The curve appears once two weekly returns have been approved.</Empty>
    );
  }

  const gridColor = print ? '#b9c6c3' : '#cdd8d5';
  const tickFill = print ? '#7d8b88' : '#8b9996';

  return (
    <div>
      <svg
        viewBox="0 0 680 210"
        role="img"
        aria-label="Physical progress, time elapsed and money released, week by week"
        style={sx('width:100%;height:auto;display:block')}
      >
        {!print ? (
          <defs>
            <linearGradient id="pmsArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#0f5c52" stopOpacity="0.34" />
              <stop offset="1" stopColor="#0f5c52" stopOpacity="0.03" />
            </linearGradient>
          </defs>
        ) : null}
        {[12, 55, 98, 141].map((y) => (
          <line key={y} x1="36" y1={y} x2="668" y2={y} stroke="#e4eae8" strokeWidth="1" />
        ))}
        <line x1="36" y1="184" x2="668" y2="184" stroke={gridColor} strokeWidth="1.4" />
        {(print
          ? ([
              [15.5, '100'],
              [101.5, '50'],
              [187.5, '0'],
            ] as const)
          : ([
              [15.5, '100'],
              [58.5, '75'],
              [101.5, '50'],
              [144.5, '25'],
              [187.5, '0'],
            ] as const)
        ).map(([y, label]) => (
          <text
            key={label}
            x="29"
            y={y}
            textAnchor="end"
            fontSize={print ? 10 : 9}
            fill={tickFill}
          >
            {label}
          </text>
        ))}
        {!print ? <polygon points={geometry.area} fill="url(#pmsArea)" /> : null}
        <polyline
          points={geometry.time}
          fill="none"
          stroke="#4b8ea8"
          strokeWidth={print ? 2.2 : 2}
          strokeDasharray="7 5"
        />
        <polyline points={geometry.fin} fill="none" stroke="#c9a227" strokeWidth={print ? 2 : 1.8} />
        <polyline
          points={geometry.actual}
          fill="none"
          stroke="#0f5c52"
          strokeWidth={print ? 3.4 : 3.2}
          strokeLinejoin="round"
          strokeLinecap={print ? undefined : 'round'}
        />
      </svg>

      {print ? (
        <div style={sx('display:flex;gap:12px;flex-wrap:wrap;margin-top:6px')}>
          <PrintKey color="#0f5c52" label="Work done" />
          <PrintKey color="#4b8ea8" label="Time elapsed" />
          <PrintKey color="#c9a227" label="Money paid on works" />
        </div>
      ) : (
        <>
          <div style={sx('display:flex;justify-content:space-between;padding:0 1.8% 0 5.3%;margin-top:-4px')}>
            {geometry.labels.map((x) => (
              <div
                key={x.key}
                style={sx("font-size:10px;color:#8b9996;font-family:'IBM Plex Mono',monospace")}
              >
                {x.label}
              </div>
            ))}
          </div>
          <div
            style={sx(
              'display:flex;gap:16px;flex-wrap:wrap;margin-top:12px;padding-top:12px;border-top:1px solid #eef2f1',
            )}
          >
            <ScreenKey color="#0f5c52" label="Physical progress" />
            <ScreenKey color="#4b8ea8" label="Time elapsed" />
            <ScreenKey color="#c9a227" label="Money paid on works" />
          </div>
        </>
      )}
    </div>
  );
}

function ScreenKey({ color, label }: { color: string; label: string }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:7px;font-size:12px;color:#33403d')}>
      <span style={{ width: '16px', height: '4px', borderRadius: '99px', background: color }} />
      {label}
    </div>
  );
}

function PrintKey({ color, label }: { color: string; label: string }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:5px;font-size:8pt;color:#33403d')}>
      <span style={{ width: '14px', height: '3px', background: color }} />
      {label}
    </div>
  );
}
